import { Defaults } from 'es-decorators'
import { isMobile } from 'init/is-mobile'
import { CUSTOM_QTY_UNITS_ATTR_ID } from 'es6/constants'
import Backbone from 'init/backbone'
import PageableCollection from 'backbone.paginator'

import isObject from 'lodash/isObject'
import extend from 'lodash/extend'
import remove from 'lodash/remove'
import each from 'lodash/each'
import map from 'lodash/map'
import isString from 'lodash/isString'
import some from 'lodash/some'
import find from 'lodash/find'
import clone from 'lodash/clone'
import isFunction from 'lodash/isFunction'
import pick from 'lodash/pick'
import isEqual from 'lodash/isEqual'
import includes from 'lodash/includes'
import bind from 'lodash/bind'
import keys from 'lodash/keys'
import omit from 'lodash/omit'
import toArray from 'lodash/toArray'
import pickBy from 'lodash/pickBy'
import has from 'lodash/has'
import isArray from 'lodash/isArray'
import difference from 'lodash/difference'
import intersection from 'lodash/intersection'
import mapValues from 'lodash/mapValues'
import union from 'lodash/union'
import { currentCurrency } from 'init/current-currency'

/**
 * Базовая коллекция для работы с пагинацией.
 */
@Defaults({
  fetchCounter: 0,
  mode: 'server',
  isScroll: false,
  loadItems: true,
  loadFilters: true,
  requestFilters: true,
  filterLoadParams: ['f', 'q', 'category_id', 'currency'],
  itemsLoadParams: ['q', 'category_id', 'currency', 'sort', 'mode', 'viewtype', 'per-page', 'page'],
  viewTypes: ['xxl', 'cards', 'list'],
  /**
   * Все подфильтровые страницы в данной категории.
   */
  filterPages: [],
  /**
   * Подфильтровые страницы, у которых совпало значение фильтров с примененными фильтрами.
   */
  filterPagesApplied: [],
  /**
   * Стандартные значения параметров, если ничего иного не задано
   */
  defaults: {
    pageSize: 20,
    sort: 'price',
    viewtype: 'list',
    f: 'null',
  },
  /**
   * Стандартное состояние, если ничего не задано.
   */
  state: {
    firstPage: 1,
    pageSize: 20,
    order: 0,
  },
  /**
   * Не выполненные ещё запросы
   */
  pendingRequests: [],
  REQUEST_TYPE_FILTERS: 'filters',
  REQUEST_TYPE_ITEMS: 'items',

  attachedOptions: ['currency', 'baseFilterPageUrl', 'filterUrlParam', 'filterPageId', 'viewTypes'],
  additionalOptions: [],
})
export class ItemPageableCollection extends PageableCollection {
  /**
   * @inheritDoc
   */
  initialize (models, options) {
    if (options && options.defaults && isObject(options.defaults)) {
      extend(this.defaults, options.defaults)
    }
    super.initialize(models, options)
    this.attachOptions(options)
    this.setQueryParams()
    this.currencyId = currentCurrency.name
    this.currencySign = currentCurrency.sign
    this.requestTypes = [this.REQUEST_TYPE_FILTERS, this.REQUEST_TYPE_ITEMS]
    // устанавливаем дефолтные свойства
    this.setDefaultProperties()
    // сохраняем выполняющиеся запросы и чистим их после выполнения
    this.listenTo(this, 'request', this.trackPendingRequests)
    this.listenTo(this, 'sync', () => {
      this.clearPendingRequest(this.REQUEST_TYPE_ITEMS)
      this.isFetching = false
    })
    this.listenTo(Backbone, 'parseFilterData', () => {
      this.clearPendingRequest(this.REQUEST_TYPE_FILTERS)
    })
  }

  /**
   * Сбрасывает this.state в начальное состояния
   */
  resetStatePageInfo () {
    this.state = omit(this.state, ['currentPage', 'nextPage', 'lastPage', 'totalPages', 'totalRecords'])
  }

  /**
   * Сохраняем переданные опции,
   * если они входят в массив attachedOptions
   * @param {Object} options
   */
  attachOptions (options) {
    if (!options) {
      return
    }
    let optionList = union(this.attachedOptions, this.additionalOptions)
    optionList.forEach(optionName => {
      if (options[optionName]) {
        this[optionName] = options[optionName]
      }
    })
  }

  /**
   * Сохраняем отправленные запросы
   *
   * @param {PageableCollection} collection
   * @param {XMLHttpRequest} xhr
   * @param {Object} options
   */
  trackPendingRequests (collection, xhr, options) {
    if (collection instanceof ItemPageableCollection) {
      collection.pendingRequests.push({
        type: options.data.filters ? this.REQUEST_TYPE_FILTERS : this.REQUEST_TYPE_ITEMS,
        xhr: xhr,
      })
    }
  }

  /**
   * Отменяет текущий запрос соответственно переданному типу
   * @param {string} type - тип запроса
   */
  cancelPendingRequest (type) {
    const pendingRequest = find(this.pendingRequests, { type: type })
    if (pendingRequest) {
      pendingRequest.xhr.abort()
      this.clearPendingRequest(type)
    }
  }

  /**
   * Отменяет текущие запросы
   */
  cancelAllPendingRequests () {
    this.requestTypes.forEach(type => {
      this.cancelPendingRequest(type)
    })
  }

  /**
   * Убирает из сохранённых запросов соответственно переданному типу
   * @param {string} type - тип запроса
   */
  clearPendingRequest (type) {
    remove(this.pendingRequests, req => req.type === type)
  }

  /**
   * Устанавливает парсеры параметров запроса
   */
  setQueryParams () {
    this.queryParams = {}
  }

  /**
   * Устанавливает дефолтные свойства коллекции
   */
  setDefaultProperties () {
    this.sort = this.defaults.sort
    this.viewtype = this.defaults.viewtype
    this.filter = this.defaults.f
  }

  /**
   * Заполнение коллекции категории моделями товаров.
   * @param {Object} resp Ответ сервера
   * @param {Object} options Опции запроса
   * @returns {Object} Models
   */
  parseRecords (resp, options) {
    const CurrentModel = this.model
    return map(resp.items, obj => {
      this.parseRecord(obj)
      each(obj.modifier_items, value => this.parseRecord(value))
      return new CurrentModel(obj, options)
    })
  }

  /**
   * Парсинг свойств полученной записи
   * @param {Object} obj
   */
  parseRecord (obj) {
    if (isString(obj.cart_item) && obj.cart_item.length > 0) {
      obj.cart_item = JSON.parse(decodeURIComponent(obj.cart_item))
    }
    if (isString(obj.wish_item) && obj.wish_item.length > 0) {
      obj.wish_item = JSON.parse(decodeURIComponent(obj.wish_item))
    }
    if (some(obj.attrs, { attr_id: CUSTOM_QTY_UNITS_ATTR_ID, boolean_value: 1 }) &&
      obj.nestedUnit &&
      obj.in_set > 0 &&
      obj.in_set !== 1) {
      obj.has_custom_qty_units = true
    }
  }

  /**
   * @inheritDoc
   */
  sync (method, model, options) {
    this.syncFilters(method, options)
    this.syncItems(method, model, options)
  }

  /**
   * Запрос моделей
   * @param {string} method
   * @param {Model} model
   * @param {Object} options
   */
  syncItems (method, model, options) {
    const pendingItemsRequest = find(this.pendingRequests, { type: 'items' })
    if (pendingItemsRequest) {
      pendingItemsRequest.xhr.abort()
      this.clearPendingRequest(this.REQUEST_TYPE_ITEMS)
    }
    if (method === 'read') {
      if (this.needItems(options.data)) {
        if (!this.links) {
          this.links = {}
        }
        super.sync(method, model, options)
      }
      return
    }
    super.sync(method, model, options)
  }

  /**
   * Запрос фильтров
   * @param {string} method
   * @param {Object} options
   */
  syncFilters (method, options) {
    if (method === 'read' && this.needFilters(options.data)) {
      const pendingFiltersRequest = find(this.pendingRequests, { type: 'filters' })
      if (pendingFiltersRequest) {
        pendingFiltersRequest.xhr.abort()
        this.clearPendingRequest(this.REQUEST_TYPE_FILTERS)
      }
      const filterOptions = {
        success: resp => {
          /*
           * filterData = resp._filters
           * filterData.totalCount = resp._meta.totalCount
           */
          Backbone.trigger('parseFilterData')
          if (options.callback && isFunction(options.callback)) {
            options.callback()
          }
        },
        url: options.url,
        data: clone(options.data),
      }
      filterOptions.data.filters = 2
      filterOptions.data.page = 1
      if (this.canRemoveItems) {
        filterOptions.data.preventCache = new Date()
      }
      super.sync('read', this, filterOptions)
    }
  }

  /**
   * Определяет необходимо ли запрашивать фильтры
   * @param {Object} allParams Параметры запроса
   * @returns {boolean} Необходимо ли запрашивать фильтры
   */
  needFilters (allParams) {
    const params = pick(allParams, this.filterLoadParams)
    if (this.requestFilters && (!isEqual(this._oldSyncFiltersParams, params) || this.loadFilters === true)) {
      this._oldSyncFiltersParams = params
      this.loadFilters = false
      return true
    }
    return false
  }

  /**
   * Определяет необходимо ли запрашивать модели
   * @param {Object} allParams
   * @returns {boolean} Необходимо ли запрашивать модели
   */
  needItems (allParams) {
    const params = pick(allParams, this.itemsLoadParams)
    if (!isEqual(this._oldSyncItemsParams, params) || this.loadItems === true) {
      this._oldSyncItemsParams = params
      return true
    }
    this.loadItems = true
    return false
  }

  /**
   * Парсинг состояния.
   * @param {Object} resp
   * @returns {Object}
   */
  parseState (resp) {
    return {
      totalRecords: resp._meta.totalCount,
      itemsInResponse: resp.items.length,
      hasDiscountItems: resp._has_discount,
    }
  }

  /**
   * Устанавливает количество моделей на странице
   * @param {number} pageSize
   * @param {Object} options
   * @param {number} page
   * @returns {PageableCollection} Объект коллекции
   */
  setPageSize (pageSize, options, page) {
    if (!pageSize) {
      return this
    }
    let state,
      totalPages,
      currentPage
    options = options || { first: false }
    state = this.state
    totalPages = Math.ceil(state.totalRecords / pageSize)
    currentPage = this.getCurrentPageByPageSize(pageSize)
    state = this.state = this._checkState(extend({}, state, {
      pageSize: pageSize,
      currentPage: options.first ? state.firstPage : currentPage,
      totalPages: totalPages,
    }))
    if (
      page !== this.state.currentPage &&
      page >= 1 && (
        page <= this.state.totalPages ||
        this.state.totalPages === 0
      )
    ) {
      this.state = this._checkState(extend({}, state, {
        currentPage: page,
      }))
    }
    return this
  }

  /**
   * Переопределен для возможности перехвата ошибок.
   * @param {Object} resp
   * @param {Object} options
   * @returns {Array} Список моделей
   */
  parse (resp, options) {
    try {
      return super.parse(resp, options)
    } catch (e) {
      if (!resp.redirect_to) {
        this.trigger('page-range-error')
      }
    }
    return this.parseRecords(resp, options)
  }

  /**
   * Вычисляет новую текущую страницу, для измененного размера страницы
   * @param {number} pageSize Новый размер страницы
   * @returns {number} Новая страница
   */
  getCurrentPageByPageSize (pageSize) {
    const currentPageSize = this.state.pageSize,
      currentPage = this.state.currentPage - 1,
      target = currentPage * currentPageSize,
      page = Math.floor(target / pageSize)
    if (page > 0) {
      return page + 1
    }
    return 1
  }

  /**
   * Устанавливает тип сортировки
   * @param {string} sort
   * @returns {undefined|PageableCollection} Возвращает объект коллекции
   */
  setSort (sort) {
    if (!sort) {
      return this
    }
    if (this.sortTypes.indexOf(sort) !== -1) {
      this.sort = sort
    } else {
      this.sort = this.defaults.sort
    }
  }

  /**
   * Устанавливает тип отображения моделей
   * @param {string} viewtype
   */
  setViewType (viewtype) {
    if (!viewtype) {
      return
    }
    if (this.viewTypes.indexOf(viewtype) !== -1) {
      this.viewtype = viewtype
    } else {
      this.viewtype = this.defaults.viewtype
    }
  }

  /**
   * Устанавливает фильтр коллекции
   * @param {string} filter
   */
  setFilter (filter) {
    if (!filter) {
      return
    }
    try {
      const parsedFilter = JSON.parse(filter)
      if (typeof parsedFilter !== 'object') {
        filter = this.defaults.f
      }
    } catch (e) {
      filter = this.defaults.f
    }
    this.filter = filter
  }

  /**
   * Устанавливает значение приоритетных товаров по id
   * @param {string} ids Идентификаторы
   */
  setPriorityIds (ids) {
    if (!ids) {
      return
    }
    this.priority_ids = ids
  }
  /**
   * Устанавливает значение приоритетных товаров по sid
   * @param {string} sids Артикулы
   */
  setPrioritySids (sids) {
    if (!sids) {
      return
    }
    this.priority_sids = sids
  }
  /**
   * Устанавливает значение приоритетных parent_id
   * @param {string} ids Идентификаторы родителей
   */
  setPriorityParentIds (ids) {
    if (!ids) {
      return
    }
    this.priority_parent_ids = ids
  }
  /**
   * Сбрасывает значение приоритетов
   */
  unsetPriority () {
    this.priority_ids = null
    this.priority_sids = null
    this.priority_parent_ids = null
  }
  /**
   * Получает объект с фильтрами модели.
   * @returns {Object} Объект с данными фильтров
   */
  getFilter () {
    let filterData
    try {
      filterData = JSON.parse(this.filter)
    } catch (e) {
      filterData = null
    }
    return filterData
  }

  /**
   * Переопределение во избежание ошибок отсутствия ивентов у fullCollection
   * @param {string} mode Настройка поведения коллекции
   * @param {Object} options Опции запроса
   * @returns {XMLHttpRequest} Запрос
   */
  switchMode (mode, options) {
    if (!includes(['server', 'client', 'infinite'], mode)) {
      throw new TypeError('`mode` must be one of "server", "client" or "infinite"')
    }
    options = options || { fetch: true, resetState: true }
    const state = this.state = options.resetState ? clone(this._initState) : this._checkState(extend({}, this.state))
    this.mode = mode
    let fullCollection = this.fullCollection,
      handler
    const handlers = this._handlers = this._handlers || {}
    if (mode !== 'server' && !fullCollection) {
      fullCollection = this._makeFullCollection(options.models || [], options)
      fullCollection.pageableCollection = this
      this.fullCollection = fullCollection
      let allHandler = this._makeCollectionEventHandler(this, fullCollection)
      if (this.fullCollection.events) {
        each(['add', 'remove', 'reset', 'sort'], event => {
          handlers[event] = handler = bind(allHandler, {}, event)
          this.on(event, handler)
          fullCollection.on(event, handler)
        })
      }
      fullCollection.comparator = this._fullComparator
    } else if (mode === 'server' && fullCollection) {
      each(keys(handlers), event => {
        handler = handlers[event]
        this.off(event, handler)
        fullCollection.off(event, handler)
      })
      delete this._handlers
      this._fullComparator = fullCollection.comparator
      delete this.fullCollection
    }
    if (mode === 'infinite') {
      const links = this.links = {},
        firstPage = state.firstPage,
        totalPages = Math.ceil(state.totalRecords / state.pageSize),
        lastPage = firstPage === 0 ? Math.max(0, totalPages - 1) : totalPages || firstPage
      for (let i = state.firstPage; i <= lastPage; i++) {
        links[i] = this.url
      }
    } else if (this.links) {
      delete this.links
    }
    return options.fetch ? this.fetch(omit(options, 'fetch', 'resetState')) : this
  }

  /**
   * Переопределяем родной fetch коллекции, чтобы считать количество
   * @param {Object} options Опции запроса
   * @returns {XMLHttpRequest} Запрос
   */
  fetch (options) {
    if (options && isObject(options)) {
      if (options.filters === true) {
        this.setLoadFilters(1)
      }
      if (options.filters === false) {
        this.setLoadFilters(0)
      }
      if (options.items === true) {
        this.setLoadItems(1)
      }
      if (options.items === false) {
        this.setLoadItems(0)
      }
      options = omit(options, ['filters', 'items'])
    }
    this.fetchCounter += 1
    if (isMobile && this.fetchCounter === 1) {
      this.setLoadFilters(0)
    }
    this.isFetching = true
    return super.fetch(options)
  }

  /**
   * Устанавливает валюту
   * @param {string} currency Название валюты
   */
  setCurrency (currency) {
    if (!currency) {
      return
    }
    this.currencyId = currency
  }

  /**
   * Устанавливает флаг необходимости обновления моделей
   * @param {string|number} loadItems Загружать ли товары
   */
  setLoadItems (loadItems) {
    this.loadItems = (parseInt(loadItems, 10) === 1)
  }

  /**
   * Устанавливает флаг необходимости обновления фильтров
   * @param {string|number} loadFilters Загружать ли фильтры
   */
  setLoadFilters (loadFilters) {
    this.loadFilters = (parseInt(loadFilters, 10) === 1)
  }

  /**
   * Есть ли примененные фильтры подфильтровых страниц.
   * @param {Object} filter
   * @returns {boolean} Есть ли примененные фильтры подфильтровых страниц
   */
  getIsAppliedFilterPage (filter) {
    let currentFilters
    if (this.baseFilterPageUrl && this.filterUrlParam) {
      currentFilters = this.filterUrlParam
    } else {
      currentFilters = this.getFilter()
    }
    return this.compareFilterObjects(currentFilters, filter)
  }

  /**
   * Сохраняем в коллекцию данные о подфильтровых страницах.
   *
   * @param {$} pages
   */
  setFilterPages (pages) {
    this.filterPages = pages.length ? toArray(pages) : []
  }

  /**
   * Обновляет статус фильтров подфильтровых страниц.
   */
  updateFilterPageStatus () {
    const filterPagesApplied = this.filterPages
      .filter(page => {
        return (!!page.filter && this.getIsAppliedFilterPage(page.filter)) ||
          page.id === this.filterPageId
      })
    if (filterPagesApplied.length) {
      Backbone.trigger('filter-page-applied', filterPagesApplied[0])
      if (this.fetchCounter === 1) {
        Backbone.trigger('change-category-wrapper')
      }
    } else {
      Backbone.trigger('filter-page-cleared')
    }
    this.filterPagesApplied = filterPagesApplied
    Backbone.trigger('update-filter-pages', filterPagesApplied)
  }

  /**
   * Сбрасывает значение фильтров подфильтровых страниц к дефолтному.
   */
  resetFilterPageFilters () {
    this.filterPagesApplied = []
  }

  /**
   * Метод для приведения значений фильтров к строке, с целью сравнения.
   *
   * @param {Object} filterObj
   *
   * @returns {Object} Отформатированный фильтр
   */
  filtersCompareFormat (filterObj) {
    return mapValues(filterObj, value => {
      if (isArray(value)) {
        value = value.map(el => String(el))
      }
      return value
    })
  }

  /**
   * Метод для сравнения объектов с фильтрами.
   *
   * @param {Object} obj
   * @param {Object} prev
   *
   * @returns {boolean} Равны или нет
   */
  compareFilterObjects (obj, prev) {
    const format = this.filtersCompareFormat,
      formattedFilters = [obj, prev].map(filterObj => {
        return format(filterObj)
      })
    return isEqual(formattedFilters[0], formattedFilters[1])
  }

  /**
   * Ищет пересечение между двумя объектами фильтров.
   *
   * @param {Object} currentFilters
   * @param {Object} filter
   *
   * @returns {boolean} Пересекаются ли фильтры
   */
  getFilterObjIntersection (currentFilters, filter) {
    return pickBy(currentFilters, function (value, key) {
      const hasValue = has(filter, key)
      let sameValue
      if (hasValue) {
        sameValue = isArray(value) ?
          !difference(intersection(filter[key], value), filter[key]).length :
          isEqual(value, filter[key])
      }
      return hasValue && sameValue
    })
  }
}
